import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { getQueryGroups, getGroupsToFilter } from "@icarius-pages/queries/selectors";
import {
    getQueryGroups as getGeoQueryGroups,
    getGroupsToFilter as getGeoGroupsToFilter,
} from "@icarius-pages/geoanalysis/selectors";
import { getUserMenuData } from "@icarius-app/selectors";
import paths, { PATHS_TO_IGNORE_IN_SEARCH } from "@icarius-router/paths";
import useIsPathAvailable from "@icarius-utils/hooks/useIsPathAvailable";
import { getLocalizedString, language } from "@icarius-localization/strings";
import useChangeLanguage from "@icarius-pages/login/hooks/useChangeLanguage";
import { requestsIds } from "@icarius-utils/requestsIds";
import DEFAULT_LIST_ITEMS from "../menu/defaultListItem";
import WbSunnyIcon from "@material-ui/icons/WbSunny";
import GetAppIcon from "@material-ui/icons/GetApp";
import TranslateIcon from "@material-ui/icons/Translate";
import StarsIcon from "@material-ui/icons/Stars";
import HomeIcon from "@material-ui/icons/Home";
import {
    WatchLaterIcon,
    WorldIcon,
    LocalAtmIcon,
    DirectionsRunIcon,
    FlightTakeoffIcon,
    InsertDriveFileIcon,
    MailIcon,
} from "@icarius-icons";

export default function useCommandRoutes({
    availablePaths,
    routes,
    customRoutes,
    handleOpenItem,
    dialogsState,
    handleToggleTheme,
    setOpen,
}) {
    const queryGroups = useSelector(getQueryGroups) || [];
    const groupsToFilter = useSelector(getGroupsToFilter) || [];
    const geoQueryGroups = useSelector(getGeoQueryGroups) || [];
    const geoGroupsToFilter = useSelector(getGeoGroupsToFilter) || [];
    const userMenuData = useSelector(getUserMenuData) || [];
    const changeLanguage = useChangeLanguage();

    const pathToGroupParent = useMemo(() => {
        const map = {};
        if (customRoutes) {
            customRoutes.forEach((group) => {
                const parent = group.parent;
                if (parent && group.children) {
                    group.children.forEach((child) => {
                        if (child.path) {
                            map[child.path] = parent;
                        }
                    });
                }
            });
        }
        return map;
    }, [customRoutes]);

    const utilsButtonIsAvailable = useIsPathAvailable(paths.utilsButton);
    const registerAssistanceButtonIsAvailable = useIsPathAvailable(paths.registerAssistanceButton);
    const myRequestsIsAvailable = useIsPathAvailable(paths.myRequests);
    const myCertificatesIsAvailable = useIsPathAvailable(paths.myCertificates);
    const modulesAndMenusIsAvailable = useIsPathAvailable(paths.modulesAndMenus);
    const accessSettingsIsAvailable = useIsPathAvailable(paths.accessSettings);

    const headerActionsRoutes = useMemo(() => {
        const actions = [];
        actions.push({
            title: getLocalizedString("changeLanguage"),
            path: "action-change-language",
            icon: <TranslateIcon />,
            isAction: true,
            action: () => {
                changeLanguage(language === "es" ? "en" : "es");
                setOpen(false);
                window.dispatchEvent(new Event("languageChanged"));
            },
        });
        actions.push({
            title: getLocalizedString("home"),
            path: "action-home",
            icon: <HomeIcon />,
            isAction: true,
            action: () => handleOpenItem({ path: paths.home }),
        });
        if (handleToggleTheme) {
            actions.push({
                title: getLocalizedString("darkMode") + " / " + getLocalizedString("lightMode"),
                path: "action-theme-toggle",
                icon: <WbSunnyIcon />,
                isAction: true,
                action: handleToggleTheme,
            });
        }
        if (registerAssistanceButtonIsAvailable && dialogsState) {
            actions.push({
                title: getLocalizedString("registerAssistance"),
                path: "action-assistance",
                icon: <WatchLaterIcon />,
                isAction: true,
                action: () => dialogsState.setAssistanceDialogIsOpen(true),
            });
        }
        if (utilsButtonIsAvailable && handleOpenItem) {
            actions.push({
                title: getLocalizedString("utilityDownloads"),
                path: "action-utils",
                icon: <GetAppIcon />,
                isAction: true,
                action: () => {
                    handleOpenItem({ path: paths.utils });
                    window.scrollTo({ top: 0 });
                },
            });
        }
        if (dialogsState) {
            actions.push({
                title: getLocalizedString("timeZones"),
                path: "action-timezones",
                icon: <WorldIcon />,
                isAction: true,
                action: () => dialogsState.setTimezoneDialogIsOpen(true),
            });
        }
        if (myRequestsIsAvailable) {
            actions.push({
                title: getLocalizedString("requestVacation"),
                path: "action-request-holidays",
                parentRoute: paths.myRequests,
                icon: <FlightTakeoffIcon />,
                isAction: true,
                action: () => handleOpenItem({ path: paths.myRequests, search: `?type=${requestsIds.holidays}` }),
            });
            actions.push({
                title: getLocalizedString("requestPermission"),
                path: "action-request-permissions",
                parentRoute: paths.myRequests,
                icon: <DirectionsRunIcon />,
                isAction: true,
                action: () => handleOpenItem({ path: paths.myRequests, search: `?type=${requestsIds.permission}` }),
            });
            actions.push({
                title: getLocalizedString("requestLoan"),
                path: "action-request-loans",
                parentRoute: paths.myRequests,
                icon: <LocalAtmIcon />,
                isAction: true,
                action: () => handleOpenItem({ path: paths.myRequests, search: `?type=${requestsIds.advance}` }),
            });
        }
        if (myCertificatesIsAvailable) {
            actions.push({
                title: getLocalizedString("Personal") + " > " + getLocalizedString("myReceiptsTitle"),
                path: "action-mis-comprobantes",
                icon: <InsertDriveFileIcon />,
                isAction: true,
                action: () => handleOpenItem({ path: paths.myCertificates }),
            });
        }
        actions.push({
            title: getLocalizedString("myDesktop") + " > " + getLocalizedString("sendMessageToManager"),
            path: "action-send-message-manager",
            icon: <MailIcon />,
            isAction: true,
            action: () => handleOpenItem({ path: paths.myDesktop || paths.home, search: "?action=openMail" }),
        });

        actions.push({
            title: getLocalizedString("myDesktop") + " > " + getLocalizedString("myMedalTable"),
            path: "action-my-medal-table",
            icon: <StarsIcon />,
            isAction: true,
            action: () => handleOpenItem({ path: paths.myDesktop || paths.home, search: "?action=openMedalTable" }),
        });

        if (modulesAndMenusIsAvailable) {
            actions.push({
                title: getLocalizedString("modules"),
                path: "action-modules-tab",
                parentRoute: paths.modulesAndMenus,
                isAction: true,
                action: () => handleOpenItem({ path: paths.modulesAndMenus, search: "?section=0" }),
            });
            actions.push({
                title: getLocalizedString("menus"),
                path: "action-menus-tab",
                parentRoute: paths.modulesAndMenus,
                isAction: true,
                action: () => handleOpenItem({ path: paths.modulesAndMenus, search: "?section=1" }),
            });
            actions.push({
                title: getLocalizedString("exclusions"),
                path: "action-exclusions-tab",
                parentRoute: paths.modulesAndMenus,
                isAction: true,
                action: () => handleOpenItem({ path: paths.modulesAndMenus, search: "?section=2" }),
            });
        }

        if (accessSettingsIsAvailable) {
            actions.push({
                title: getLocalizedString("newManager"),
                path: "action-accessSettings-newManager",
                parentRoute: paths.accessSettings,
                isAction: true,
                action: () => handleOpenItem({ path: paths.accessSettings, search: "?action=newManager" }),
            });
            actions.push({
                title: getLocalizedString("accessMap"),
                path: "action-accessSettings-accessMap",
                parentRoute: paths.accessSettings,
                isAction: true,
                action: () => handleOpenItem({ path: paths.accessSettings, search: "?action=accessMap" }),
            });
        }

        const queriesParent = routes.find((r) => r.path === paths.queries) ? paths.queries : null;
        const myQueriesParent = routes.find((r) => r.path === paths.myQueries) ? paths.myQueries : null;
        const parentToUse = queriesParent || myQueriesParent;

        if (parentToUse && queryGroups && queryGroups.length > 0) {
            queryGroups.forEach((group) => {
                if (!groupsToFilter.includes(group.toLowerCase())) {
                    actions.push({
                        title: group,
                        path: `action-query-group-${group}`,
                        parentRoute: parentToUse,
                        isAction: true,
                        action: () => handleOpenItem({ path: parentToUse, search: `?group=${group}` }),
                    });
                }
            });
        }

        const geoParent = routes.find((r) => r.path === paths.geoanalysis) ? paths.geoanalysis : null;

        if (geoParent && geoQueryGroups && geoQueryGroups.length > 0) {
            geoQueryGroups.forEach((group) => {
                if (!geoGroupsToFilter.includes(group.toLowerCase())) {
                    actions.push({
                        title: group,
                        path: `action-geo-group-${group}`,
                        parentRoute: geoParent,
                        isAction: true,
                        action: () => handleOpenItem({ path: geoParent, search: `?group=${group}` }),
                    });
                }
            });
        }

        const csrParent = routes.find((r) => r.path === paths.collaboratorSkillsRanking)
            ? paths.collaboratorSkillsRanking
            : null;
        if (csrParent) {
            const csrBaseTitle =
                getLocalizedString("organizationalDevelopment") +
                " > " +
                getLocalizedString("trainingAndDevelopment") +
                " > " +
                getLocalizedString("skillsRanking") +
                " > ";
            actions.push({
                title: csrBaseTitle + getLocalizedString("collaboratorRanking"),
                path: "action-csr-0",
                isAction: true,
                action: () => handleOpenItem({ path: csrParent, search: "?section=0" }),
            });
            actions.push({
                title: csrBaseTitle + getLocalizedString("skillMatrix"),
                path: "action-csr-1",
                isAction: true,
                action: () => handleOpenItem({ path: csrParent, search: "?section=1" }),
            });
            actions.push({
                title: csrBaseTitle + getLocalizedString("suggestedCourses"),
                path: "action-csr-2",
                isAction: true,
                action: () => handleOpenItem({ path: csrParent, search: "?section=2" }),
            });
        }

        if (userMenuData && userMenuData.length > 0) {
            const userMenuParent = paths.userMenu;
            userMenuData.forEach((menu, menuIndex) => {
                if (menu.items && menu.items.length > 0) {
                    menu.items.forEach((item, itemIndex) => {
                        actions.push({
                            title: menu.name + " > " + item.name,
                            path: `action-usermenu-${menuIndex}-${itemIndex}`,
                            parentRoute: userMenuParent,
                            isAction: true,
                            action: () =>
                                handleOpenItem({
                                    path: userMenuParent,
                                    search: `?index=${menuIndex}&autoOpenIndex=${itemIndex}`,
                                }),
                        });
                    });
                }
            });
        }

        return actions;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        handleToggleTheme,
        dialogsState,
        registerAssistanceButtonIsAvailable,
        utilsButtonIsAvailable,
        myRequestsIsAvailable,
        myCertificatesIsAvailable,
        modulesAndMenusIsAvailable,
        accessSettingsIsAvailable,
        handleOpenItem,
        changeLanguage,
        routes,
        queryGroups,
        groupsToFilter,
        geoQueryGroups,
        geoGroupsToFilter,
        userMenuData,
        setOpen,
    ]);

    const formattedRoutes = useMemo(() => {
        const mappedDefaultItems = DEFAULT_LIST_ITEMS.map((item) => ({
            ...item,
            title: getLocalizedString(item.title),
        })).filter((route) => availablePaths?.includes(route.path));

        const overriddenPaths = mappedDefaultItems.map((i) => i.path);
        const filteredRoutes = (routes || [])
            .filter((r) => !r.path || !overriddenPaths.includes(r.path))
            .filter((route) => availablePaths?.includes(route.path));

        const auxRoutes = [...filteredRoutes, ...mappedDefaultItems, ...headerActionsRoutes];

        const routeMap = {};
        auxRoutes.forEach((r) => {
            if (r.path) routeMap[r.path] = r;
        });

        return auxRoutes
            .filter((route) => !route.isGroup && !PATHS_TO_IGNORE_IN_SEARCH.includes(route.path))
            .flatMap((route, index) => {
                const parents = [route.parentRoute];
                if (route.extraParents) {
                    parents.push(...route.extraParents);
                }

                return parents.map((parent, pIndex) => {
                    const baseTitle = route.title || route.string || route.path || "Ruta sin nombre";
                    let label = baseTitle;

                    if (parent) {
                        const parentRouteObj = routeMap[parent];
                        const parentTitle = parentRouteObj?.title || parentRouteObj?.string;
                        const groupParent = pathToGroupParent[parent];
                        if (parentTitle) {
                            if (groupParent?.title) {
                                label = `${groupParent.title} > ${parentTitle} > ${baseTitle}`;
                            } else {
                                label = `${parentTitle} > ${baseTitle}`;
                            }
                        } else {
                            if (groupParent?.title) {
                                label = `${groupParent.title} > ${baseTitle}`;
                            } else {
                                label = baseTitle;
                            }
                        }
                    } else {
                        const groupParent = pathToGroupParent[route.path];
                        if (groupParent?.title) {
                            label = `${groupParent.title} > ${baseTitle}`;
                        }
                    }

                    const resolvedIcon =
                        route.icon ||
                        (parent && routeMap[parent]?.icon) ||
                        pathToGroupParent[parent || route.path]?.icon ||
                        null;

                    const searchLabelSpacing = label.replace(/[-_]/g, " ");
                    const searchLabelNoSpacing = label.replace(/[-_]/g, "");

                    return {
                        id: (route.path || index) + (pIndex > 0 ? `-alias-${pIndex}` : ""),
                        path: route.path,
                        label: label,
                        searchLabelSpacing,
                        searchLabelNoSpacing,
                        icon: resolvedIcon,
                        originalRoute: route,
                    };
                });
            })
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [availablePaths, routes, pathToGroupParent, headerActionsRoutes]);

    return formattedRoutes;
}
